import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalItem, ModalPageView } from '@cotera/client/app/components/app';
import { DataGridCategoryBreakdown } from '@cotera/client/app/components/ui/data-grid';
import type {
  DataGridCategoryExpandRequest,
  DataGridStatBucket,
  DataGridStructuredFilterValue,
} from '@cotera/client/app/components/ui/data-grid';

/**
 * Opens a categorical column's full "+ N more" breakdown in the app modal
 * stack. The data-grid header stays generic — it only raises an
 * {@link DataGridCategoryExpandRequest} through `onExpandCategory`; the preview
 * (which lives inside the app's providers) hands it to this action's `.ask()`.
 *
 * Invoked programmatically from the header, never from the command palette, so
 * `applicable` returns false and there is no shortcut.
 */
export class ViewCategoryBreakdownAction extends BaseAction<DataGridCategoryExpandRequest> {
  title = 'View category breakdown';

  inputSchema = z.object({
    columnLabel: z.string(),
    categoryCount: z.number(),
    total: z.number(),
    filterValue: z.custom<unknown>(),
    buckets: z.custom<DataGridStatBucket[]>(),
    onSelectFilter: z.custom<(value: DataGridStructuredFilterValue) => void>(),
  });

  constructor(
    private readonly modalManager: { push: (item: ModalItem) => void }
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return false;
  }

  ask(
    payload: Partial<DataGridCategoryExpandRequest>
  ): AskResponse<DataGridCategoryExpandRequest> {
    const columnLabel = payload.columnLabel ?? '';
    const buckets = payload.buckets ?? [];
    const categoryCount = payload.categoryCount ?? buckets.length;
    return {
      view: ({ onCancel }) => {
        this.modalManager.push({
          view: () => (
            <ModalPageView
              title={`All ${categoryCount.toLocaleString()} ${columnLabel} categories`}
              className="px-4 py-4"
            >
              <DataGridCategoryBreakdown
                buckets={buckets}
                columnLabel={columnLabel}
                filterValue={payload.filterValue}
                total={payload.total ?? 0}
                onSelect={payload.onSelectFilter ?? (() => {})}
              />
            </ModalPageView>
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }

  async execute(): Promise<ActionResult<{ t: string }[]>> {
    // No-op: the whole point is the modal opened in `ask`.
    return ok({});
  }
}
